// Desktop table of users.
import { Trash, PencilSimple, Key, DotsThree } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useT } from '@/i18n/LanguageContext';
import { ROLE_META, fmtDate, RoleBadge, StatusPill } from './userMeta';

export const UserTable = ({ users, currentUserId, employeeName, onToggleActive, onResetPw, onEdit, onDelete }) => {
  const { t } = useT();
  return (
    <section className="hidden md:block bg-white rounded-lg border border-zinc-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">{t('users.th.user')}</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">{t('users.th.role')}</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">{t('users.linkedEmployee')}</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">{t('users.th.status')}</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">{t('users.createdAt')}</th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">{t('users.th.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {users.map((u) => {
              const isSelf = u.id === currentUserId;
              const meta = ROLE_META[u.role] || ROLE_META.user;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-zinc-50/60 transition-colors">
                  <td className="px-5 py-3 align-middle">
                    <div className="flex items-center gap-3">
                      <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-zinc-100 text-zinc-700 text-sm font-semibold shrink-0">
                        {(u.name || u.email || '?').slice(0, 1).toUpperCase()}
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${meta.dot}`} />
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-zinc-900 truncate flex items-center gap-1.5">
                          {u.name}
                          {isSelf && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">{t('users.self')}</span>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-500 truncate">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 align-middle"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-3 align-middle text-zinc-700">{u.employee_id ? employeeName(u.employee_id) : '—'}</td>
                  <td className="px-5 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
                    <StatusPill
                      active={u.is_active}
                      disabled={isSelf}
                      onClick={() => onToggleActive(u)}
                      testid={`toggle-active-${u.id}`}
                      title={isSelf ? t('users.selfStateLock') : (u.is_active ? t('users.deactivate') : t('users.activate'))}
                    />
                  </td>
                  <td className="px-5 py-3 align-middle text-zinc-500 tabular-nums">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3 align-middle text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="操作" data-testid={`user-row-menu-${u.id}`}>
                          <DotsThree size={18} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => onEdit(u)} data-testid={`edit-${u.id}`} className="gap-2">
                          <PencilSimple size={14} /> {t('users.menu.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onResetPw(u)} data-testid={`reset-pw-${u.id}`} className="gap-2">
                          <Key size={14} /> {t('users.resetPwTitle')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onDelete(u)} disabled={isSelf} data-testid={`delete-${u.id}`} className="gap-2 text-rose-600 focus:text-rose-600">
                          <Trash size={14} /> {t('users.menu.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
